import React from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import { SAFE_AREA, TAP_TARGET, mediaPhone } from "@/shared/theme/breakpoints";
import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import { AppButton } from "@/shared/components";
import type { CampaignEngagementTerms } from "@/api/engagementTerms";

const Footer = styled.footer`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 16px;
	border-top: 1px solid #e6e6e6;
	background: #fefefe;

	/* Phones: status on its own line, then the actions sharing the full width. */
	${mediaPhone`
		flex-wrap: wrap;
		gap: 8px;
		padding: 10px 16px calc(12px + ${SAFE_AREA.bottom});
	`}
`;

const Updated = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 4px 12px;
	font-size: 12px;
	color: #707070;

	/* Phones: who and when each stay whole; the date drops to its own line when the two don't fit. */
	${mediaPhone`
		flex: 1 1 100%;
		flex-wrap: wrap;
		gap: 0 6px;
		padding: 0;

		> span {
			white-space: nowrap;
		}
	`}
`;
const AcceptedDot = styled.span`
	display: grid;
	place-items: center;
	width: 14px;
	height: 14px;
	margin-right: 1px;
	border-radius: 50%;
	background: rgba(27, 158, 90, 0.16);
	color: #1b9e5a;
	font-size: 9px;
`;

const Actions = styled.div`
	display: flex;
	gap: 8px;

	.ant-btn {
		width: 128px;
		height: 40px;
		border-radius: 64px;
		font-weight: 600;
	}

	.anticon {
		font-size: 16px;
	}

	/*
	 * Phones: 44px actions that fill the width and never clip. When all three (Cancel Changes, Save, Send
	 * to Creator) don't fit one row, Send to Creator wraps onto a full-width row of its own.
	 */
	${mediaPhone`
		flex: 1 1 100%;
		flex-wrap: wrap;

		/* AppButton is a plain <button>; !important beats Save's inline desktop width. */
		.ant-btn,
		> button {
			flex: 1 1 auto;
			width: auto !important;
			min-width: 104px;
			height: ${TAP_TARGET}px;
			padding: 0 14px;
			white-space: nowrap;
		}
	`}
`;

interface Props {
	updatedAt: string | null;
	locked: boolean;
	dirty: boolean;
	loading: boolean;
	saving: boolean;
	submitting: boolean;
	canSend: boolean;
	onReset: () => void;
	onSave: () => void;
	onSend: () => void;
	creatorMode?: boolean;
	version?: CampaignEngagementTerms | null;
	isLatestVersion?: boolean;
	onIssueVersion?: () => void;
	unsavedVersion?: boolean;
}

export const EngagementTermsFooter: React.FC<Props> = ({
	updatedAt,
	locked,
	dirty,
	loading,
	saving,
	submitting,
	canSend,
	onReset,
	onSave,
	onSend,
	creatorMode = false,
	version = null,
	isLatestVersion = false,
	onIssueVersion,
	unsavedVersion = false,
}) => {
	const editorBusy = loading || saving || submitting;
	const accepted = creatorMode && version?.responseStatus === "accepted";
	const pending = creatorMode && version?.responseStatus === "pending";
	const canIssue =
		creatorMode &&
		isLatestVersion &&
		["pending", "accepted", "declined"].includes(version?.responseStatus || "") &&
		!!onIssueVersion;
	const changesDisabled = creatorMode ? (!dirty && !unsavedVersion) || editorBusy : !dirty || editorBusy;
	return (
		<Footer>
			<Updated>
				{accepted && (
					<AcceptedDot>
						<CheckOutlined />
					</AcceptedDot>
				)}
				{accepted ? (
					<>
						<span>Accepted by {version.respondedByName || "Creator"}</span>
						<span>
							{version.respondedAt ? dayjs(version.respondedAt).format("MMM DD, YYYY · h:mm A") : ""}
						</span>
					</>
				) : pending ? (
					`Sent to Creator${version?.presentedAt ? ` · ${dayjs(version.presentedAt).format("MMM DD, YYYY · h:mm A")}` : ""}`
				) : updatedAt ? (
					`Last updated: ${dayjs(updatedAt).format("MMM DD, YYYY · h:mm A")}`
				) : (
					"Not saved yet"
				)}
			</Updated>
			{canIssue && (
				<Actions>
					<AppButton variant="outline" onClick={onIssueVersion} disabled={editorBusy}>
						<PlusOutlined /> Issue new version
					</AppButton>
				</Actions>
			)}
			{!locked && (
				<Actions>
					<AppButton variant="outline" onClick={onReset} disabled={changesDisabled}>
						Cancel Changes
					</AppButton>
					<AppButton variant="primary" onClick={onSave} disabled={changesDisabled} style={{ width: "128px" }}>
						{saving ? "Saving..." : "Save"}
					</AppButton>
					{canSend && (
						<AppButton variant="primary" onClick={onSend} disabled={editorBusy}>
							Send to Creator
						</AppButton>
					)}
				</Actions>
			)}
		</Footer>
	);
};
